import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import MoreVert from "@mui/icons-material/MoreVert";
import { useMemo, useState } from "react";
import DataTable from "components/Tables/DataTable/DataTable";
import MDButton from "components/MDButton";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import { ARCHIVE_PRODUCT, PRODUCTS, SAVE_PRODUCT, SETTINGS } from "./api";
import { blankWorksheet, Worksheet, worksheetInput } from "./calculator";
import WorksheetEditor from "./WorksheetEditor";
import WorksheetProductTypeSelect from "./WorksheetProductTypeSelect";

type Product = {
  id: string;
  definition: Worksheet;
  organizationProductType?: { id: string; name: string };
};
export default function WorksheetProducts({ productTypeId }: { productTypeId: string }) {
  const { data, loading, error, refetch } = useQuery(PRODUCTS);
  const { data: settings } = useQuery(SETTINGS);
  const { filtering } = usePaginatedVariables();
  const [save] = useMutation(SAVE_PRODUCT);
  const [archive, { loading: archiving }] = useMutation(ARCHIVE_PRODUCT);
  const [editing, setEditing] = useState<{
    id?: string;
    definition: Worksheet;
    productTypeId: string;
  } | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; product: Product } | null>(null);
  const [failure, setFailure] = useState("");
  const canManage = !!settings?.worksheetSettings.canManageProducts;
  const edit = (product: Product) =>
    setEditing({
      id: product.id,
      definition: product.definition,
      productTypeId: product.organizationProductType?.id ?? productTypeId,
    });
  const rows = useMemo(
    () =>
      (data?.worksheetProducts ?? [])
        .filter(
          (product: Product) =>
            product.organizationProductType?.id === productTypeId &&
            product.definition.name
              .toLowerCase()
              .includes(filtering.debouncedSearchTerm?.toLowerCase() ?? "")
        )
        .map((product: Product) => ({ ...product, name: product.definition.name }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name)),
    [data, productTypeId, filtering.debouncedSearchTerm]
  );
  const columns = [
    {
      Header: "Product Name",
      accessor: "name",
      width: "80%",
      Cell: ({ value, row }: any) => (
        <Button
          variant="text"
          sx={{ color: "inherit", textTransform: "none", fontWeight: "inherit", p: 0 }}
          onClick={() => edit(row.original)}
        >
          {value}
        </Button>
      ),
    },
    {
      Header: "Actions",
      accessor: "id",
      Cell: ({ row }: any) =>
        canManage ? (
          <Box display="flex" justifyContent="center">
            <IconButton
              aria-label={`Actions for ${row.original.name}`}
              disabled={!!editing || archiving}
              onClick={(e) => setMenu({ anchor: e.currentTarget, product: row.original })}
            >
              <MoreVert />
            </IconButton>
          </Box>
        ) : null,
    },
  ];
  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;
  return (
    <Stack spacing={2}>
      {!editing && canManage && (
        <Box display="flex" justifyContent="flex-end">
          <MDButton
            color="info"
            variant="gradient"
            onClick={() =>
              setEditing({
                definition: blankWorksheet(settings?.worksheetSettings.markup),
                productTypeId,
              })
            }
          >
            Create Worksheet Product
          </MDButton>
        </Box>
      )}
      {editing && (
        <Box>
          <Box sx={{ mb: 2, maxWidth: 400 }}>
            <WorksheetProductTypeSelect
              value={editing.productTypeId}
              disabled={!canManage}
              onChange={(value) => setEditing({ ...editing, productTypeId: value })}
            />
          </Box>
          <WorksheetEditor
            key={editing.id ?? "new"}
            initial={editing.definition}
            readOnly={!canManage}
            saveLabel="Save product"
            onSave={async (input) => {
              if (!editing.productTypeId) throw Error("Select a Product Type before saving.");
              await save({
                variables: {
                  id: editing.id,
                  productTypeId: editing.productTypeId,
                  input: worksheetInput(input),
                },
              });
              await refetch();
              setEditing(null);
            }}
          />
          <Button onClick={() => setEditing(null)}>Close editor</Button>
        </Box>
      )}
      {failure && <Alert severity="error">{failure}</Alert>}
      <DataTable table={{ columns, rows }} canSearch filtering={filtering} />
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) edit(menu.product);
            setMenu(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu)
              setEditing({
                definition: {
                  ...worksheetInput(menu.product.definition),
                  name: `${menu.product.definition.name} (copy)`,
                },
                productTypeId: menu.product.organizationProductType?.id ?? productTypeId,
              });
            setMenu(null);
          }}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={async () => {
            const id = menu?.product.id;
            setMenu(null);
            if (!id) return;
            setFailure("");
            try {
              await archive({ variables: { id } });
              await refetch();
            } catch (e) {
              setFailure((e as Error).message);
            }
          }}
        >
          Archive
        </MenuItem>
      </Menu>
    </Stack>
  );
}
